import { blue, bold, brandColor, dim, gray, white } from "./colors.mjs";
import { CancelError } from "./error.mjs";
import SelectRefreshablePrompt from "./select-list.mjs";
import { stdout } from "./streams.mjs";
import { cancel, crash, logRaw, newline, shapes, space, status, stripAnsi } from "./index.mjs";
import { ConfirmPrompt, MultiSelectPrompt, SelectPrompt, TextPrompt, isCancel } from "@clack/core";
import { createLogUpdate } from "log-update";

//#region interactive.ts
const logUpdate = createLogUpdate(stdout);
const grayBar = gray(shapes.bar);
const blCorner = gray(shapes.corners.bl);
const leftT = gray(shapes.leftT);
function acceptDefault(promptConfig, renderers, initialValue) {
	const error = promptConfig.validate?.(initialValue);
	if (error) {
		const errorMessage = error instanceof Error ? error.message : error;
		if (promptConfig.throwOnError) throw new Error(errorMessage);
		else crash(errorMessage);
	}
	logRaw(renderers.submit({ value: initialValue }).join("\n"));
	return initialValue;
}
const inputPrompt = async (promptConfig) => {
	const renderers = {
		...getRenderers(promptConfig),
		...promptConfig.renderers
	};
	let prompt;
	const dispatchRender = (props, p) => {
		let state = props.state;
		if (state === "initial" && promptConfig.initialErrorMessage) {
			state = "error";
			props.error = promptConfig.initialErrorMessage;
		}
		return renderers[state](props, p).join("\n");
	};
	if (promptConfig.type === "select") {
		const initialValue = String(promptConfig.defaultValue);
		if (promptConfig.acceptDefault) return acceptDefault(promptConfig, renderers, initialValue);
		prompt = new SelectPrompt({
			...promptConfig,
			options: promptConfig.options.filter((o) => !o.hidden),
			initialValue,
			render() {
				return dispatchRender(this, prompt);
			}
		});
	} else if (promptConfig.type === "confirm") {
		const initialValue = Boolean(promptConfig.defaultValue);
		if (promptConfig.acceptDefault) return acceptDefault(promptConfig, renderers, initialValue);
		prompt = new ConfirmPrompt({
			...promptConfig,
			initialValue,
			active: promptConfig.activeText || "",
			inactive: promptConfig.inactiveText || "",
			render() {
				return dispatchRender(this, prompt);
			}
		});
	} else if (promptConfig.type == "multiselect") {
		let initialValues;
		if (Array.isArray(promptConfig.defaultValue)) initialValues = promptConfig.defaultValue;
		else if (promptConfig.defaultValue !== void 0) initialValues = [String(promptConfig.defaultValue)];
		if (promptConfig.acceptDefault) return acceptDefault(promptConfig, renderers, initialValues);
		prompt = new MultiSelectPrompt({
			...promptConfig,
			options: promptConfig.options,
			initialValues,
			render() {
				return dispatchRender(this, prompt);
			}
		});
	} else if (promptConfig.type === "list") {
		const initialValue = String(promptConfig.defaultValue);
		if (promptConfig.acceptDefault) return acceptDefault(promptConfig, renderers, initialValue);
		prompt = new SelectRefreshablePrompt({
			...promptConfig,
			onRefresh: promptConfig.onRefresh ?? (() => Promise.resolve(promptConfig.options)),
			initialValue,
			render() {
				return dispatchRender(this, prompt);
			}
		});
	} else {
		const initialValue = promptConfig.initialValue ?? String(promptConfig.defaultValue ?? "");
		if (promptConfig.acceptDefault) return acceptDefault(promptConfig, renderers, initialValue);
		prompt = new TextPrompt({
			...promptConfig,
			initialValue: promptConfig.initialValue,
			defaultValue: String(promptConfig.defaultValue ?? ""),
			render() {
				return dispatchRender(this, prompt);
			}
		});
	}
	const input = await prompt.prompt();
	if (isCancel(input)) if (promptConfig.throwOnError) throw new CancelError("Operation cancelled");
	else {
		cancel("Operation cancelled");
		process.exit(0);
	}
	return input;
};
const renderSubmit = (config, value) => {
	const { question, label } = config;
	if (config.type !== "confirm" && !value) return [`${leftT} ${question} ${dim("(skipped)")}`, `${grayBar}`];
	const content = config.type === "confirm" ? `${grayBar} ${brandColor(value)} ${dim(label)}` : `${grayBar} ${brandColor(label)} ${dim(value)}`;
	return [
		`${leftT} ${question}`,
		content,
		`${grayBar}`
	];
};
const getRenderers = (config) => {
	switch (config.type) {
		case "select": return getSelectRenderers(config);
		case "confirm": return getConfirmRenderers(config);
		case "text": return getTextRenderers(config);
		case "multiselect": return getSelectRenderers(config);
		case "list": return getSelectListRenderers(config);
	}
};
const getTextRenderers = (config) => {
	const { question } = config;
	const helpText = config.helpText ?? "";
	const format = config.format ?? ((val) => String(val));
	const defaultValue = config.defaultValue?.toString() ?? "";
	const activeRenderer = (props) => {
		const { userInputWithCursor } = props;
		return [
			`${blCorner} ${bold(question)} ${dim(helpText)}`,
			`${space(2)}${format(userInputWithCursor || dim(defaultValue))}`,
			``
		];
	};
	return {
		initial: () => [
			`${blCorner} ${bold(question)} ${dim(helpText)}`,
			`${space(2)}${gray(format(defaultValue))}`,
			``
		],
		active: activeRenderer,
		error: ({ value, error }) => [
			`${leftT} ${status.error} ${dim(error)}`,
			`${grayBar}`,
			`${blCorner} ${question} ${dim(helpText)}`,
			`${space(2)}${format(value)}`,
			``
		],
		submit: ({ value }) => renderSubmit(config, format(value ?? "")),
		cancel: activeRenderer
	};
};
const getSelectRenderers = (config) => {
	const { options, question, helpText: _helpText } = config;
	const helpText = _helpText ?? "";
	const maxItemsPerPage = config.maxItemsPerPage ?? 32;
	const defaultRenderer = ({ cursor = 0, value }) => {
		const renderOption = (opt, i) => {
			const { label: optionLabel, value: optionValue } = opt;
			const active = i === cursor;
			const isInListOfValues = Array.isArray(value) && value.includes(optionValue);
			const color = isInListOfValues || active ? blue : white;
			const text = active ? color.underline(optionLabel) : color(optionLabel);
			const sublabel = opt.sublabel ? color.grey(opt.sublabel) : "";
			const indicator = isInListOfValues || active && !Array.isArray(value) ? color(opt.activeIcon ?? shapes.radioActive) : color(opt.inactiveIcon ?? shapes.radioInactive);
			return `${space(2)}${indicator} ${text} ${sublabel}`;
		};
		const renderOptionCondition = (_, i) => {
			if (options.length <= maxItemsPerPage) return true;
			if (i < cursor) return options.length - i <= maxItemsPerPage;
			return cursor + maxItemsPerPage > i;
		};
		const visibleOptions = options.filter((o) => !o.hidden);
		const activeOption = visibleOptions.at(cursor);
		const lines = [
			`${blCorner} ${bold(question)} ${dim(helpText)}`,
			`${cursor > 0 && options.length > maxItemsPerPage ? `${space(2)}${dim("...")}\n` : ""}${visibleOptions.map(renderOption).filter(renderOptionCondition).join(`\n`)}${cursor + maxItemsPerPage < options.length && options.length > maxItemsPerPage ? `\n${space(2)}${dim("...")}` : ""}`,
			``
		];
		if (activeOption?.description) {
			const wordSegmenter = new Intl.Segmenter("en", { granularity: "word" });
			const padding = space(2);
			const availableWidth = process.stdout.columns - stripAnsi(padding).length * 2;
			const description = stripAnsi(activeOption.description);
			const descriptionLines = [];
			let descriptionLineNumber = 0;
			for (const data of wordSegmenter.segment(description)) {
				let line = descriptionLines[descriptionLineNumber] ?? "";
				if (line.length + data.segment.length > availableWidth) {
					descriptionLineNumber++;
					line = "";
					if (data.segment.match(/^\s+$/)) continue;
				}
				descriptionLines[descriptionLineNumber] = line + data.segment;
			}
			lines.push(dim(descriptionLines.map((line) => padding + line + padding).join("\n")), ``);
		}
		return lines;
	};
	return {
		initial: defaultRenderer,
		active: defaultRenderer,
		confirm: defaultRenderer,
		error: (opts, prompt) => {
			return [
				`${leftT} ${status.error} ${dim(opts.error)}`,
				`${grayBar}`,
				...defaultRenderer(opts, prompt)
			];
		},
		submit: ({ value }) => {
			if (Array.isArray(value)) return renderSubmit(config, options.filter((o) => value.includes(o.value)).map((o) => o.label).join(", "));
			return renderSubmit(config, options.find((o) => o.value === value)?.label);
		},
		cancel: defaultRenderer
	};
};
const getSelectListRenderers = (config) => {
	const { question, helpText: _helpText } = config;
	let options = config.options;
	const helpText = _helpText ?? "";
	const { rows } = stdout;
	const defaultRenderer = ({ cursor, value }, prompt) => {
		if (prompt instanceof SelectRefreshablePrompt) options = prompt.options;
		cursor = cursor ?? 0;
		let smallCursor = 0;
		const renderOption = (opt, i) => {
			const { label: optionLabel, value: optionValueAny } = opt;
			const optionValue = optionValueAny.toString();
			const active = i === smallCursor;
			const isInListOfValues = Array.isArray(value) && value.includes(optionValue);
			const color = isInListOfValues || active ? blue : white;
			const text = active ? color.underline(optionLabel) : color(optionLabel);
			const indicator = isInListOfValues || active && !Array.isArray(value) ? color(shapes.radioActive) : color(shapes.radioInactive);
			const indicatorMargin = 2;
			const detailBulletpointMargin = indicatorMargin + 4;
			return [`${space(indicatorMargin)}${indicator} ${text}`, ...opt.details.map((detail, j) => `${space(detailBulletpointMargin)}${j === opt.details.length - 1 ? gray(shapes.corners.bl) : grayBar} ${detail}`)];
		};
		const pages = [];
		let current = {
			size: 0,
			options: []
		};
		const VERTICAL_MARGIN = 6;
		for (const option of options) {
			const optionHeight = option.details.length + 1;
			if (current.size + optionHeight > rows - VERTICAL_MARGIN) {
				pages.push(current.options);
				current = {
					size: optionHeight,
					options: [option]
				};
				continue;
			}
			current.size += optionHeight;
			current.options.push(option);
		}
		if (current.size !== 0) pages.push(current.options);
		let isFirstPage = true;
		let isLastPage = false;
		let page = [];
		let len = 0;
		for (let i = 0; i < pages.length; i++) {
			const pageIter = pages[i];
			if (cursor >= len && pageIter.length + len > cursor) {
				isFirstPage = i === 0;
				isLastPage = i === pages.length - 1;
				page = pageIter;
				smallCursor = cursor - len;
				break;
			}
			len += pageIter.length;
		}
		return [
			`${blCorner} ${bold(question)} ${dim(helpText)}`,
			...!isFirstPage ? [`${space(2)}${dim("...")}`] : [],
			...page.map(renderOption).reduce((prev, now) => [...prev, ...now], []),
			...!isLastPage ? [`${space(2)}${dim("...")}`] : []
		];
	};
	return {
		initial: defaultRenderer,
		active: defaultRenderer,
		confirm: defaultRenderer,
		error: (opts, prompt) => {
			return [
				`${leftT} ${status.error} ${dim(opts.error)}`,
				`${grayBar}`,
				...defaultRenderer(opts, prompt)
			];
		},
		submit: ({ value }) => {
			if (Array.isArray(value)) return renderSubmit(config, options.filter((o) => value.includes(o.value)).map((o) => o.value).join(", "));
			return renderSubmit(config, options.find((o) => o.value === value)?.value);
		},
		cancel: defaultRenderer
	};
};
const getConfirmRenderers = (config) => {
	const { activeText, inactiveText, question, helpText: _helpText } = config;
	const helpText = _helpText ?? "";
	const active = activeText || "Yes";
	const inactive = inactiveText || "No";
	const defaultRenderer = ({ value }) => {
		const yesColor = value ? blue.underline : dim;
		const noColor = value ? dim : blue.underline;
		return [`${blCorner} ${bold(question)} ${dim(helpText)}`, `${space(2)}${yesColor(active)} / ${noColor(inactive)}`];
	};
	return {
		initial: defaultRenderer,
		active: defaultRenderer,
		confirm: defaultRenderer,
		error: defaultRenderer,
		submit: ({ value }) => renderSubmit(config, value ? "yes" : "no"),
		cancel: defaultRenderer
	};
};
const spinnerFrames = {
	clockwise: [
		"┤",
		"┘",
		"┴",
		"└",
		"├",
		"┌",
		"┬",
		"┐"
	],
	vertical: [
		"▁",
		"▃",
		"▄",
		"▅",
		"▆",
		"▇",
		"▆",
		"▅",
		"▄",
		"▃"
	]
};
const ellipsisFrames = [
	"",
	".",
	"..",
	"...",
	" ..",
	"  .",
	""
];
const spinner = (frames = spinnerFrames.clockwise, color = brandColor) => {
	const frameRate = 120;
	let loop = null;
	let startMsg;
	let currentMsg;
	function clearLoop() {
		if (loop) clearTimeout(loop);
		loop = null;
	}
	return {
		start(msg, helpText) {
			helpText ||= ``;
			currentMsg = msg;
			startMsg = currentMsg;
			if (helpText !== void 0 && helpText.length > 0) startMsg += ` ${dim(helpText)}`;
			if (isInteractive()) {
				let index = 0;
				clearLoop();
				loop = setInterval(() => {
					index++;
					const spinnerFrame = frames[index % frames.length];
					const ellipsisFrame = ellipsisFrames[index % ellipsisFrames.length];
					if (msg) logUpdate(`${color(spinnerFrame)} ${currentMsg} ${ellipsisFrame}`);
				}, frameRate);
			} else logRaw(`${leftT} ${startMsg}`);
		},
		update(msg) {
			currentMsg = msg;
		},
		stop(msg) {
			if (isInteractive()) {
				logUpdate.clear();
				if (msg) {
					logUpdate(`${leftT} ${startMsg}\n${grayBar} ${msg}`);
					logUpdate.done();
					newline();
				}
				clearLoop();
			} else {
				if (msg !== void 0) logRaw(`${grayBar} ${msg}`);
				newline();
			}
		}
	};
};
const unwrapFactory = (input) => {
	return typeof input === "function" ? input() : input;
};
const spinnerWhile = async (opts) => {
	const s = opts.spinner ?? spinner();
	s.start(unwrapFactory(opts.startMessage));
	try {
		return await unwrapFactory(opts.promise);
	} finally {
		s.stop(unwrapFactory(opts.endMessage));
	}
};
/**
* Test whether the process is "interactive".
*/
function isInteractive() {
	try {
		return Boolean(process.stdin.isTTY && process.stdout.isTTY);
	} catch {
		return false;
	}
}

//#endregion
export { blCorner, getRenderers, grayBar, inputPrompt, isInteractive, leftT, spinner, spinnerFrames, spinnerWhile };